import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Bell, BellOff, Trophy, Swords, Heart, Award, Save, Loader2, LogOut, Shield, Trash2, AlertTriangle, Camera, X, Lock, Mail, AtSign, Check, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
  });
  const [notifSettings, setNotifSettings] = useState({
    push_enabled: true,
    league_updates: true,
    tournament_updates: true,
    social_updates: true,
    badge_updates: true
  });
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  
  // New states for password, email, username changes
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  const [emailData, setEmailData] = useState({ new_email: "", password: "" });
  const [changingEmail, setChangingEmail] = useState(false);
  const [usernameData, setUsernameData] = useState({ new_username: "" });
  const [changingUsername, setChangingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameChangesRemaining, setUsernameChangesRemaining] = useState(3);

  useEffect(() => {
    // Check if push notifications are supported
    if ('Notification' in window && 'serviceWorker' in navigator) {
      setPushSupported(true);
      setPushEnabled(Notification.permission === 'granted');
    }

    // Fetch notification settings
    const fetchSettings = async () => {
      try {
        const response = await api.getNotificationSettings();
        setNotifSettings(response.data);
      } catch (error) {
        console.error("Error fetching notification settings:", error);
      }
    };
    fetchSettings();
    
    // Fetch username changes remaining
    const fetchUsernameChanges = async () => {
      try {
        const response = await api.getUsernameChangesRemaining();
        setUsernameChangesRemaining(response.data.remaining);
      } catch (error) {
        console.error("Error fetching username changes:", error);
      }
    };
    fetchUsernameChanges();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.updateProfile(formData);
      setUser(response.data);
      toast.success("Profil güncellendi!");
    } catch (error) {
      toast.error("Güncelleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Lütfen bir görsel dosyası seçin");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Görsel çok büyük (max 2MB)");
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64 = e.target?.result;
          const response = await api.uploadProfilePicture(base64);
          setUser(response.data);
          toast.success("Profil fotoğrafı güncellendi!");
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fotoğraf yüklenemedi");
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.onerror = () => {
        toast.error("Dosya okunamadı");
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Hata oluştu");
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setUploadingPhoto(true);
    try {
      const response = await api.deleteProfilePicture();
      setUser(response.data);
      toast.success("Profil fotoğrafı kaldırıldı");
    } catch (error) {
      toast.error("Fotoğraf kaldırılamadı");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleEnablePush = async () => {
    if (!pushSupported) {
      toast.error("Tarayıcınız push bildirimleri desteklemiyor");
      return;
    }

    try {
      // Service worker'ı kaydet
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      
      // İzin iste
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Bildirim izni verilmedi");
        return;
      }
      
      // Push subscription oluştur
      const VAPID_PUBLIC_KEY = 'BJBEIiDT69qJFJ80w-zjxHGr4FYCKcSTt2c73K_bgr-nbUrxMnE3M9leGP7Q2qAFAd7EhzXZ5HAryumoCO07d4s';
      
      // Base64 URL to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      
      // Backend'e gönder
      await api.subscribeToPush({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      });
      
      setPushEnabled(true);
      toast.success("Push bildirimleri aktif edildi!");
      
      // Update backend
      await api.updateNotificationSettings({ push_enabled: true });
      setNotifSettings(prev => ({ ...prev, push_enabled: true }));
      
    } catch (error) {
      console.error('Push notification error:', error);
      toast.error("Push bildirimleri aktif edilemedi: " + (error.message || "Bilinmeyen hata"));
    }
  };

  const handleDisablePush = async () => {
    try {
      await api.unsubscribeFromPush();
      setPushEnabled(false);
      setNotifSettings(prev => ({ ...prev, push_enabled: false }));
      toast.success("Push bildirimleri devre dışı bırakıldı");
    } catch (error) {
      toast.error("Hata oluştu");
    }
  };

  const updateNotificationSetting = async (key, value) => {
    setNotifLoading(true);
    try {
      await api.updateNotificationSettings({ [key]: value });
      setNotifSettings(prev => ({ ...prev, [key]: value }));
      toast.success("Ayar güncellendi");
    } catch (error) {
      toast.error("Güncelleme başarısız");
    } finally {
      setNotifLoading(false);
    }
  };

  // Password change handler
  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error("Tüm alanları doldurun");
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    if (passwordData.new.length < 8) {
      toast.error("Yeni şifre en az 8 karakter olmalı");
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.changePassword({
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success("Şifre başarıyla değiştirildi!");
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setChangingPassword(false);
    }
  };

  // Email change handler
  const handleChangeEmail = async () => {
    if (!emailData.new_email || !emailData.password) {
      toast.error("Email ve şifre gerekli");
      return;
    }
    
    setChangingEmail(true);
    try {
      const response = await api.changeEmail(emailData);
      setUser(response.data);
      toast.success("Email başarıyla değiştirildi!");
      setEmailData({ new_email: "", password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email değiştirilemedi");
    } finally {
      setChangingEmail(false);
    }
  };

  // Check username availability
  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    
    setCheckingUsername(true);
    try {
      const response = await api.checkUsernameAvailability(username);
      setUsernameAvailable(response.data);
    } catch (error) {
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  // Username change handler
  const handleChangeUsername = async () => {
    if (!usernameData.new_username) {
      toast.error("Yeni kullanıcı adı gerekli");
      return;
    }
    if (!usernameAvailable?.available) {
      toast.error("Bu kullanıcı adı kullanılamaz");
      return;
    }
    
    setChangingUsername(true);
    try {
      const response = await api.changeUsername(usernameData);
      setUser(response.data.user);
      setUsernameChangesRemaining(response.data.remaining_changes);
      toast.success(response.data.message);
      setUsernameData({ new_username: "" });
      setUsernameAvailable(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kullanıcı adı değiştirilemedi");
    } finally {
      setChangingUsername(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "SİL") {
      toast.error("Lütfen onay için SİL yazın");
      return;
    }

    setDeleting(true);
    try {
      await api.deleteAccount();
      toast.success("Hesabınız başarıyla silindi");
      setDeleteDialogOpen(false);
      await logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hesap silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
          Ayarlar
        </h1>
        <p className="text-[#B1C1C1] mt-1">Hesap ayarlarınızı yönetin</p>
      </div>

      {/* Profile Settings */}
      <form onSubmit={handleSubmit}>
        <div className="glass-card p-6 space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-5 h-5 text-[#4DFFFF]" />
            <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
              Profil
            </h2>
          </div>

          {/* Profile Picture */}
          <div className="flex items-center gap-6">
            <div className="relative group">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#4DFFFF]/20 to-[#4DFFFF]/5 flex items-center justify-center border-2 border-[#4DFFFF]/30 overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-[#4DFFFF]">
                    {user?.name?.charAt(0)}
                  </span>
                )}
              </div>
              
              {/* Overlay buttons */}
              <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="p-2 rounded-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/80 transition-colors"
                  title="Fotoğraf yükle"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                </button>
                {user?.picture && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    disabled={uploadingPhoto}
                    className="p-2 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
                    title="Fotoğrafı kaldır"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="photo-upload-input"
              />
            </div>
            
            <div className="flex-1">
              <p className="text-white font-medium">{user?.email}</p>
              <p className="text-sm text-[#64748B] mb-2">@{user?.username}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="border-[#4DFFFF]/30 text-[#4DFFFF] hover:bg-[#4DFFFF]/10"
                data-testid="change-photo-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {user?.picture ? "Fotoğrafı Değiştir" : "Fotoğraf Ekle"}
              </Button>
            </div>
          </div>

          {/* Name */}
          <div>
            <Label htmlFor="name" className="text-[#B1C1C1]">Ad Soyad</Label>
            <div className="relative mt-2">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
              />
            </div>
          </div>

          {/* Bio */}
          <div>
            <Label htmlFor="bio" className="text-[#B1C1C1]">Hakkında</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Kendiniz hakkında bir şeyler yazın..."
              className="mt-2 bg-[#050E2E]/50 border-white/10 text-white"
              rows={4}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 font-['Barlow_Condensed'] uppercase tracking-wider font-bold h-12"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Kaydet
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Username Change */}
      <div className="glass-card p-6 mt-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <AtSign className="w-5 h-5 text-[#9333EA]" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Kullanıcı Adı
          </h2>
        </div>
        
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-200">
              Kullanıcı adınızı yılda en fazla 3 kez değiştirebilirsiniz. 
              <span className="font-bold"> Bu yıl {usernameChangesRemaining} hakkınız kaldı.</span>
            </p>
          </div>
        </div>
        
        <div>
          <Label htmlFor="username" className="text-[#B1C1C1]">Mevcut: @{user?.username}</Label>
          <div className="relative mt-2">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              id="username"
              value={usernameData.new_username}
              onChange={(e) => {
                const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                setUsernameData({ new_username: value });
                checkUsernameAvailability(value);
              }}
              placeholder="Yeni kullanıcı adı"
              className="pl-10 pr-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
              maxLength={20}
            />
            {checkingUsername && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] animate-spin" />
            )}
            {!checkingUsername && usernameAvailable !== null && (
              usernameAvailable.available ? (
                <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
              ) : (
                <X className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
              )
            )}
          </div>
          {usernameAvailable && !usernameAvailable.available && (
            <p className="text-red-400 text-xs mt-1">{usernameAvailable.reason}</p>
          )}
          {usernameAvailable?.available && (
            <p className="text-green-400 text-xs mt-1">Bu kullanıcı adı müsait!</p>
          )}
        </div>
        
        <Button
          onClick={handleChangeUsername}
          disabled={changingUsername || !usernameAvailable?.available || usernameChangesRemaining <= 0}
          className="w-full bg-[#9333EA] text-white hover:bg-[#9333EA]/90 h-12"
        >
          {changingUsername ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <AtSign className="w-4 h-4 mr-2" />
          )}
          Kullanıcı Adını Değiştir
        </Button>
      </div>

      {/* Email Change */}
      <div className="glass-card p-6 mt-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-5 h-5 text-[#4DFFFF]" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Email Değiştir
          </h2>
        </div>
        
        <div>
          <Label className="text-[#B1C1C1]">Mevcut: {user?.email}</Label>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              type="email"
              value={emailData.new_email}
              onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
              placeholder="Yeni email adresi"
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
            />
          </div>
        </div>
        
        <div>
          <Label className="text-[#B1C1C1]">Onay için şifreniz</Label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              type="password"
              value={emailData.password}
              onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
              placeholder="Mevcut şifreniz"
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
            />
          </div>
        </div>
        
        <Button
          onClick={handleChangeEmail}
          disabled={changingEmail}
          className="w-full bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90 h-12"
        >
          {changingEmail ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Mail className="w-4 h-4 mr-2" />
          )}
          Email Değiştir
        </Button>
      </div>

      {/* Password Change */}
      <div className="glass-card p-6 mt-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-5 h-5 text-[#C6A87C]" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Şifre Değiştir
          </h2>
        </div>
        
        <div>
          <Label className="text-[#B1C1C1]">Mevcut Şifre</Label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              type="password"
              value={passwordData.current}
              onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
              placeholder="Mevcut şifreniz"
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
            />
          </div>
        </div>
        
        <div>
          <Label className="text-[#B1C1C1]">Yeni Şifre</Label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              type="password"
              value={passwordData.new}
              onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
              placeholder="En az 8 karakter"
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
            />
          </div>
        </div>
        
        <div>
          <Label className="text-[#B1C1C1]">Yeni Şifre (Tekrar)</Label>
          <div className="relative mt-2">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
            <Input
              type="password"
              value={passwordData.confirm}
              onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
              placeholder="Şifreyi tekrar girin"
              className="pl-10 bg-[#050E2E]/50 border-white/10 text-white h-12"
            />
          </div>
        </div>
        
        <Button
          onClick={handleChangePassword}
          disabled={changingPassword}
          className="w-full bg-[#C6A87C] text-[#050E2E] hover:bg-[#C6A87C]/90 h-12"
        >
          {changingPassword ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Lock className="w-4 h-4 mr-2" />
          )}
          Şifreyi Değiştir
        </Button>
      </div>

      {/* Notification Settings */}
      <div className="glass-card p-6 mt-6 space-y-6">
        <div className="flex items-center gap-2 mb-2">
          <Bell className="w-5 h-5 text-[#C6A87C]" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Bildirimler
          </h2>
        </div>

        {/* Push Notifications Master Toggle */}
        <div className="p-4 rounded-lg bg-white/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {pushEnabled ? (
                <Bell className="w-5 h-5 text-[#4DFFFF]" />
              ) : (
                <BellOff className="w-5 h-5 text-[#64748B]" />
              )}
              <div>
                <p className="text-white font-medium">Push Bildirimleri</p>
                <p className="text-xs text-[#64748B]">
                  {pushSupported ? "Tarayıcı bildirimleri alın" : "Tarayıcınız desteklemiyor"}
                </p>
              </div>
            </div>
            {pushEnabled ? (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDisablePush}
                className="border-red-500/50 text-red-400"
                data-testid="disable-push-btn"
              >
                Kapat
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleEnablePush}
                disabled={!pushSupported}
                className="bg-[#4DFFFF] text-[#050E2E]"
                data-testid="enable-push-btn"
              >
                Aktif Et
              </Button>
            )}
          </div>
        </div>

        {/* Notification Categories */}
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-[#C6A87C]" />
              <div>
                <p className="text-white">Lig Güncellemeleri</p>
                <p className="text-xs text-[#64748B]">Haftalık sonuçlar, sıralama değişiklikleri</p>
              </div>
            </div>
            <Switch
              checked={notifSettings.league_updates}
              onCheckedChange={(checked) => updateNotificationSetting('league_updates', checked)}
              disabled={notifLoading}
              data-testid="switch-league-updates"
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div className="flex items-center gap-3">
              <Swords className="w-5 h-5 text-[#9333EA]" />
              <div>
                <p className="text-white">Turnuva Güncellemeleri</p>
                <p className="text-xs text-[#64748B]">Maç sonuçları, yeni turlar</p>
              </div>
            </div>
            <Switch
              checked={notifSettings.tournament_updates}
              onCheckedChange={(checked) => updateNotificationSetting('tournament_updates', checked)}
              disabled={notifLoading}
              data-testid="switch-tournament-updates"
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div className="flex items-center gap-3">
              <Heart className="w-5 h-5 text-[#F61225]" />
              <div>
                <p className="text-white">Sosyal Güncellemeler</p>
                <p className="text-xs text-[#64748B]">Yeni takipçiler, mesajlar</p>
              </div>
            </div>
            <Switch
              checked={notifSettings.social_updates}
              onCheckedChange={(checked) => updateNotificationSetting('social_updates', checked)}
              disabled={notifLoading}
              data-testid="switch-social-updates"
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-[#FFD700]" />
              <div>
                <p className="text-white">Rozet Bildirimleri</p>
                <p className="text-xs text-[#64748B]">Yeni rozetler kazandığında</p>
              </div>
            </div>
            <Switch
              checked={notifSettings.badge_updates}
              onCheckedChange={(checked) => updateNotificationSetting('badge_updates', checked)}
              disabled={notifLoading}
              data-testid="switch-badge-updates"
            />
          </div>
        </div>
      </div>

      {/* Account Actions */}
      <div className="glass-card p-6 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-red-400" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-white">
            Hesap
          </h2>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Çıkış Yap
        </Button>
      </div>

      {/* Danger Zone - Delete Account */}
      <div className="glass-card p-6 mt-6 border-red-500/30">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-red-400">
            Tehlikeli Bölge
          </h2>
        </div>
        <p className="text-sm text-[#B1C1C1] mb-4">
          Hesabınızı sildiğinizde tüm verileriniz (ligler, turnuvalar, mesajlar, rozetler) kalıcı olarak silinecektir. Bu işlem geri alınamaz.
        </p>
        <Button
          onClick={() => setDeleteDialogOpen(true)}
          variant="outline"
          className="w-full border-red-500 text-red-500 hover:bg-red-500/20"
          data-testid="delete-account-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Hesabımı Sil
        </Button>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#0A1635] border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-red-400 font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Hesabı Kalıcı Olarak Sil
            </DialogTitle>
            <DialogDescription className="text-[#B1C1C1]">
              Bu işlem geri alınamaz. Tüm verileriniz silinecektir:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <ul className="text-sm text-red-300 space-y-1">
                <li>• Oluşturduğunuz tüm ligler</li>
                <li>• Katıldığınız turnuvalar</li>
                <li>• Mesajlarınız ve bildirimleriniz</li>
                <li>• Kazandığınız rozetler</li>
                <li>• Takipçi ve takip ettikleriniz</li>
              </ul>
            </div>
            
            <div>
              <Label className="text-[#B1C1C1] text-sm">
                Onaylamak için <span className="text-red-400 font-bold">SİL</span> yazın:
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="SİL"
                className="mt-2 bg-[#050E2E]/50 border-red-500/30 text-white"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteConfirmText("");
              }}
              className="border-white/20 text-white"
            >
              İptal
            </Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmText !== "SİL"}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Hesabı Sil
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
